import React, { useEffect, useState } from "react";
import api, { formatINR, apiErrorMsg } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Plus, Bell, Pencil, Trash2, CheckCircle, Users, ListChecks, MessageSquare, BadgeCheck, UserPlus, Package, BarChart3, Tag, FileText, Building2, Star, ScrollText, Upload, Lock, Unlock, ShieldCheck, X } from "lucide-react";
import { toast } from "sonner";
import { DashboardSkeleton } from "../components/Skeletons";
import ShortlistsTab from "./ShortlistsTab";
// v2.47 — AdminEnquiriesTab is no longer rendered directly here; it lives inside
// LeadsCRMTab as the "Form Enquiries" sub-tab. Import dropped to avoid dead code.
import AdminDealersTab from "./AdminDealersTab";
import AdminCreateUserTab from "./AdminCreateUserTab";
import AdminOverviewTab from "./AdminOverviewTab";
import AdminPlansTab from "./AdminPlansTab";
import AdminUnifiedPlansTab from "./AdminUnifiedPlansTab";
import AdminContentTab from "./AdminContentTab";
import AdminTestimonialsTab from "./AdminTestimonialsTab";
import AdminProjectsTab from "./AdminProjectsTab";
import AdminTeamTab from "./AdminTeamTab";
import AdminFiltersTab from "./AdminFiltersTab";
import AdminLocationsTab from "./AdminLocationsTab";
import AdminApprovalsTab from "./AdminApprovalsTab";
import AdminPagesTab from "./AdminPagesTab";
import AdminSettingsTab from "./AdminSettingsTab";
import AdminTransactionsTab from "./AdminTransactionsTab";
import LeadsCRMTab from "./LeadsCRMTab";
import BulkImportDialog from "./BulkImportDialog";
// v2.71.69 — Heavy form (1744 lines); only renders when an admin clicks
// Add/Edit. Lazy-loaded to keep /admin overview bundle slim.
const UnifiedSubmissionForm = React.lazy(() => import("../components/UnifiedSubmissionForm"));
import { BulkSelectBar, RowCheckbox } from "../components/BulkSelectBar";
import AdminPermissionsModal from "./AdminPermissionsModal";
import SharePlansButton from "../components/SharePlansButton";
import PropertyCard from "../shared/components/PropertyCard";
import ViewToggle, { useViewToggle } from "../shared/components/ViewToggle";
import AdminShortlistFlow from "../shared/components/AdminShortlistFlow";
import AdminListingsTab from "./AdminListingsTab";
import { emitDataChange, useDataSync } from "../lib/dataSync";

function PropertyForm({ open, onClose, onSaved, initial, initialStep }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-sm" data-testid="property-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight text-2xl">{initial?.id ? "Edit Property" : "Add Property"}</DialogTitle>
        </DialogHeader>
        <React.Suspense fallback={<div className="p-8 text-center text-sm text-slate-500">Loading form…</div>}>
          <UnifiedSubmissionForm
            role="admin"
            initial={initial}
            showAdminFlags
            onSaved={() => { onSaved?.(); onClose?.(); }}
            lockedFields={initial?.locked_fields || []}
            initialStep={initialStep}
          />
        </React.Suspense>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminPage({ defaultTab, embedded = false } = {}) {
  const { user, isAdmin, isTeam, loading } = useAuth();
  const adminUserId = user?.id || "";
  const canAccess = isAdmin || isTeam;
  const loc = useLocation();
  const nav = useNavigate();
  // v2.71.32 — `defaultTab` (from route wrapper) wins; falls back to legacy ?tab=
  const rawTab = defaultTab || new URLSearchParams(loc.search).get("tab") || "overview";
  // Friendly URL aliases — keep external deep-links working.
  // v2.71.33 — Added `approvals` + `add-client` mappings (route segment names
  // differ from internal tab values "pending" / "create-user") so the new
  // routes render the correct TabsContent block instead of a blank panel.
  const TAB_ALIAS = {
    leads: "crm",
    enquiries: "crm",
    approvals: "pending",
    "add-client": "create-user",
  };
  const queryTab = TAB_ALIAS[rawTab] || rawTab;
  // v2.71.33 — Reverse map: when an internal tab value differs from the new
  // route segment, this map produces the route-friendly name for embedded
  // navigation. Anything not in here is passed through verbatim.
  const TAB_TO_ROUTE = { pending: "approvals", "create-user": "add-client" };
  // v2.71.32 — When `embedded`, tab changes navigate via routes (/admin/<tab>);
  // otherwise legacy ?tab= behaviour preserved for backwards-compat.
  const onTabSwitch = (v) => {
    if (embedded) nav(`/admin/${TAB_TO_ROUTE[v] || v}`, { replace: true });
    else nav(`/admin?tab=${v}`, { replace: true });
  };
  const [properties, setProperties] = useState([]);
  const [pending, setPending] = useState([]);
  const [users, setUsers] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editStep, setEditStep] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  // v2.48 — Per-section bulk-delete selection state
  const [selectedListings, setSelectedListings] = useState(new Set());
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [selectedNotifs, setSelectedNotifs] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  // v2.54 — Per-user permissions modal
  const [permTarget, setPermTarget] = useState(null);

  // v2.71.71 — Card/Table view toggle for the Listings tab. Default cards.
  const [listingsView, setListingsView] = useViewToggle("admin-listings", "cards");
  // v2.71.55 — Admin shortlist flow (bulk-add from listings selection)
  const [bulkShortlistOpen, setBulkShortlistOpen] = useState(false);
  // v2.72 — Per-tab data needs (embedded route mode). Each admin sub-route
  // used to fire ALL four list endpoints (incl. the full /properties dump)
  // on every mount — the main reason the console felt laggy. Now only the
  // data the active tab actually renders is fetched.
  const EMBEDDED_NEEDS = { users: ["users"], notifications: ["notifications"], shortlists: ["properties"] };
  const load = async () => {
    if (!canAccess) return;
    const wants = embedded
      ? (EMBEDDED_NEEDS[queryTab] || [])
      : ["properties", "pending", "users", "notifications"];
    if (wants.length === 0) return;
    const CALLS = {
      properties: () => api.get("/properties"),
      pending: () => api.get("/admin/properties/pending"),
      users: () => api.get("/admin/users"),
      notifications: () => api.get("/admin/notifications"),
    };
    // v2.71.98 — Promise.allSettled so a single endpoint failure (e.g.
    // legacy /admin/notifications) never blanks the Users/Listings tab.
    const results = await Promise.allSettled(wants.map((w) => CALLS[w]()));
    results.forEach((res, i) => {
      if (res.status !== "fulfilled") return;
      const w = wants[i];
      if (w === "properties") setProperties(res.value.data.results || []);
      if (w === "pending") setPending(res.value.data || []);
      if (w === "users") setUsers(Array.isArray(res.value.data) ? res.value.data : []);
      if (w === "notifications") setNotifications(res.value.data || []);
    });
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [isAdmin, queryTab]);

  // v2.72 — Central invalidation: any mutation anywhere (this tab, another
  // component, another browser tab, or another session via the WS piggyback)
  // re-fetches whatever this page currently renders. load() is tab-scoped,
  // so this stays cheap.
  useDataSync(["properties", "users", "approvals"], load);

  // v2.71.34 — Auto-open Add-Property dialog when the route arrives with
  // `?add=1` (used by the Admin Overview "Add Property" CTAs). After opening
  // we strip the param so a refresh / browser-back does not re-open the
  // dialog unexpectedly.
  // v2.71.101 — Also handle `?edit=<id>` deep-link (from AdminApprovalsTab
  // "Open in full editor" button) — fetch the property and open the form.
  // v2.71.103 — Scope this handler to /admin/listings only. AdminPage is
  // mounted on every /admin/* sub-route via withAdmin(); without this guard
  // the handler fires on /admin/projects?edit=<project_id> (and others) and
  // wrongly issues GET /api/properties/<project_id>, which 404s and toasts
  // "Property not found". Each sub-tab now owns its own ?edit handler.
  useEffect(() => {
    const params = new URLSearchParams(loc.search);
    if (params.get("add") === "1" && isAdmin) {
      setEditing(null);
      setShowForm(true);
      params.delete("add");
      const next = params.toString();
      nav(`${loc.pathname}${next ? `?${next}` : ""}`, { replace: true });
      return;
    }
    const isListingsRoute = loc.pathname === "/admin/listings"
      || (loc.pathname === "/admin" && (defaultTab === "listings" || params.get("tab") === "listings"));
    const editId = params.get("edit");
    if (editId && isAdmin && isListingsRoute) {
      const stepParam = params.get("step");
      (async () => {
        try {
          const r = await api.get(`/properties/${editId}`);
          setEditStep(stepParam === "location" ? 2 : null);
          setEditing(r.data);
          setShowForm(true);
        } catch (e) {
          toast.error(apiErrorMsg(e, "Property not found"));
        }
        params.delete("edit");
        params.delete("step");
        const next = params.toString();
        nav(`${loc.pathname}${next ? `?${next}` : ""}`, { replace: true });
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.search, loc.pathname, isAdmin, defaultTab]);

  // v2.71.9 — URL-driven filters for Admin tables (Listings + Users).
  // Reads `?status=Sold`, `?best_deal=true`, `?premium=true` (listings)
  // and `?role=client`, `?filter=active_subs` (users). Reset via the
  // inline "Clear filter" chip rendered above each filtered table.
  const qs = new URLSearchParams(loc.search);
  const fListingStatus = qs.get("status") || "";
  const fListingBest = qs.get("best_deal") === "true";
  const fListingPremium = qs.get("premium") === "true";
  const fUserRole = qs.get("role") || "";
  const fUserFilter = qs.get("filter") || "";

  const filteredProperties = React.useMemo(() => {
    let arr = properties;
    if (fListingStatus) arr = arr.filter((p) => (p.status || "") === fListingStatus);
    if (fListingBest) arr = arr.filter((p) => !!p.best_deal);
    if (fListingPremium) arr = arr.filter((p) => !!(p.premium_deal || p.priority));
    return arr;
  }, [properties, fListingStatus, fListingBest, fListingPremium]);

  const filteredUsers = React.useMemo(() => {
    let arr = users;
    if (fUserRole) arr = arr.filter((u) => (u.role || "") === fUserRole);
    if (fUserFilter === "active_subs") {
      const now = Date.now();
      arr = arr.filter((u) => {
        const tier = (u.subscription?.tier || u.tier || "").toLowerCase();
        if (!tier || tier === "normal" || tier === "free" || tier === "basic") return false;
        const exp = u.subscription?.expires_at || u.subscription_expires_at;
        if (!exp) return true; // tier set, no expiry → treat active
        try { return new Date(exp).getTime() > now; } catch { return true; }
      });
    }
    return arr;
  }, [users, fUserRole, fUserFilter]);

  // v2.71.98 — Users tab pagination + role-dropdown reset.
  const USERS_PER_PAGE = 20;
  const [userPage, setUserPage] = useState(1);
  // Reset to page 1 whenever the filter changes (otherwise we could land on
  // an empty page after narrowing the set down)
  React.useEffect(() => { setUserPage(1); }, [fUserRole, fUserFilter]);
  const userPageCount = Math.max(1, Math.ceil(filteredUsers.length / USERS_PER_PAGE));
  const pagedUsers = React.useMemo(
    () => filteredUsers.slice((userPage - 1) * USERS_PER_PAGE, userPage * USERS_PER_PAGE),
    [filteredUsers, userPage]
  );

  // v2.71.98 — Update the `role=` URL param without losing other params.
  const setRoleFilter = (role) => {
    const next = new URLSearchParams(loc.search);
    if (!role || role === "all") next.delete("role"); else next.set("role", role);
    nav(`${loc.pathname}?${next.toString()}`, { replace: true });
  };

  const clearAdminFilter = (keys) => {
    const next = new URLSearchParams(loc.search);
    keys.forEach((k) => next.delete(k));
    nav(`${loc.pathname}?${next.toString()}`, { replace: true });
  };
  const hasListingFilter = fListingStatus || fListingBest || fListingPremium;
  const hasUserFilter = fUserRole || fUserFilter;

  if (loading) return <DashboardSkeleton stats={4} rows={6} />;
  if (!canAccess) return <Navigate to="/login" replace state={{ from: "/admin" }} />;

  const del = async (id) => {
    if (!window.confirm("Delete this property?")) return;
    await api.delete(`/properties/${id}`);
    toast.success("Deleted");
    emitDataChange(["properties", "approvals", "dashboard"], { action: "delete", id });
  };

  const markSold = async (id) => {
    if (!window.confirm("Mark this property as Sold? It will auto-hide after 15 days.")) return;
    await api.post(`/admin/properties/${id}/mark-sold`);
    toast.success("Marked Sold");
    emitDataChange(["properties", "dashboard"], { action: "patch", id, patch: { status: "Sold" } });
  };

  const markAvailable = async (id) => {
    await api.post(`/admin/properties/${id}/mark-available`);
    toast.success("Marked Available");
    emitDataChange(["properties", "dashboard"], { action: "patch", id, patch: { status: "Available" } });
  };

  const approve = async (id) => {
    await api.post(`/admin/properties/${id}/approve`);
    toast.success("Approved");
    emitDataChange(["properties", "approvals", "dashboard"], { action: "patch", id, patch: { approval_status: "approved" } });
  };

  const reject = async (id) => {
    const reason = window.prompt("Reason for rejection? (optional)");
    await api.post(`/admin/properties/${id}/reject`, reason ? { reason } : {});
    toast.success("Rejected");
    emitDataChange(["properties", "approvals", "dashboard"], { action: "patch", id, patch: { approval_status: "rejected" } });
  };

  // v2.70 Phase 3 — Toggle is_featured on a listing
  const toggleFeatured = async (p) => {
    try {
      await api.patch(`/admin/properties/${p.id}/featured`, { is_featured: !p.is_featured });
      toast.success(p.is_featured ? "Removed from Featured" : "Marked as Featured");
      emitDataChange(["properties", "dashboard"], { action: "patch", id: p.id, patch: { is_featured: !p.is_featured } });
    } catch (e) {
      toast.error("Couldn't toggle Featured");
    }
  };

  const toggleBlock = async (u) => {
    try {
      await api.patch(`/admin/users/${u.id}`, { blocked: !u.blocked });
      toast.success(u.blocked ? "User unblocked" : "User blocked");
      emitDataChange(["users", "dashboard"]);
    } catch (e) { toast.error(apiErrorMsg(e, "Failed")); }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete user ${u.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("User deleted");
      emitDataChange(["users", "dashboard"]);
    } catch (e) { toast.error(apiErrorMsg(e, "Failed")); }
  };

  // v2.48 — Bulk handlers + single delete for notifications
  const bulkDeleteListings = async (ids) => {
    setBulkBusy(true);
    try {
      const r = await api.post("/admin/properties/bulk-delete", { ids });
      toast.success(`Deleted ${r.data.deleted} listing${r.data.deleted !== 1 ? "s" : ""}`);
      emitDataChange(["properties", "approvals", "dashboard"]);
    } catch (e) { toast.error(apiErrorMsg(e, "Bulk delete failed")); }
    setBulkBusy(false);
  };
  const bulkDeleteUsers = async (ids) => {
    setBulkBusy(true);
    try {
      const r = await api.post("/admin/users/bulk-delete", { ids });
      const skippedNote = r.data.skipped > 0 ? ` (${r.data.skipped} skipped)` : "";
      toast.success(`Deleted ${r.data.deleted} user${r.data.deleted !== 1 ? "s" : ""}${skippedNote}`);
      emitDataChange(["users", "dashboard"]);
    } catch (e) { toast.error(apiErrorMsg(e, "Bulk delete failed")); }
    setBulkBusy(false);
  };
  const bulkDeleteNotifs = async (ids) => {
    setBulkBusy(true);
    try {
      const r = await api.post("/admin/notifications/bulk-delete", { ids });
      toast.success(`Deleted ${r.data.deleted} notification${r.data.deleted !== 1 ? "s" : ""}`);
      load();
    } catch (e) { toast.error(apiErrorMsg(e, "Bulk delete failed")); }
    setBulkBusy(false);
  };
  const deleteNotif = async (id) => {
    if (!window.confirm("Delete this notification?")) return;
    try {
      await api.delete(`/admin/notifications/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(apiErrorMsg(e, "Failed")); }
  };
  const clearReadNotifs = async () => {
    if (!window.confirm("Delete all READ notifications?")) return;
    try {
      const r = await api.post("/admin/notifications/clear-read");
      toast.success(`Cleared ${r.data.deleted} read notification${r.data.deleted !== 1 ? "s" : ""}`);
      load();
    } catch (e) { toast.error(apiErrorMsg(e, "Failed")); }
  };

  const unreadCount = notifications.filter((n) => !n.read).length;

  const markAllRead = async () => {
    await api.post("/admin/notifications/read-all");
    load();
  };

  return (
    <div className={embedded ? "" : "min-h-screen bg-[#FAFAFA]"}>
      <div className={embedded ? "" : "max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-10 py-8"}>
        {isTeam && (
          <div className="mb-4 p-3 bg-[#F0F9FF] border border-[#BAE6FD] text-[#075985] text-[12px] rounded-sm inline-flex items-center gap-2" data-testid="team-mode-banner">
            <BadgeCheck size={13} /> Team Mode — read-only access. Lead status updates allowed; structural changes (plans, content, users) admin-only.
          </div>
        )}
        {!embedded && (
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6">
            <div>
              <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#0055FF] mb-1">{isTeam ? "Team Console" : "Admin Console"}</div>
              <h1 className="font-heading font-bold text-3xl tracking-tight">{isTeam ? "Operations View" : "Property Management"}</h1>
            </div>
            {isAdmin && (
              <div className="flex items-center gap-2 flex-wrap">
                <SharePlansButton variant="outline" size="sm" />
                <Button variant="outline" onClick={() => setShowBulk(true)} className="rounded-sm" data-testid="bulk-import-btn">
                  <Upload size={15} className="mr-1.5" /> Bulk Import
                </Button>
                <Button onClick={() => { setEditing(null); setShowForm(true); }} className="btn-brand rounded-sm" data-testid="add-property-btn">
                  <Plus size={15} className="mr-1.5" /> Add Property
                </Button>
              </div>
            )}
            {isTeam && !isAdmin && (
              <div className="flex items-center gap-2">
                <SharePlansButton variant="outline" size="sm" />
              </div>
            )}
          </div>
        )}
        {/* v2.71.33 — In embedded mode (route-based DashboardLayout) the
            console header / h1 is owned by the layout. We still surface the
            critical admin CTAs (Share Plans · Bulk Import · Add Property)
            inline above the tabs so admins do not lose primary actions. */}
        {embedded && isAdmin && (
          <div className="flex items-center gap-2 flex-wrap justify-end mb-4" data-testid="admin-embedded-actions">
            <SharePlansButton variant="outline" size="sm" />
            <Button variant="outline" onClick={() => setShowBulk(true)} className="rounded-sm" data-testid="bulk-import-btn">
              <Upload size={15} className="mr-1.5" /> Bulk Import
            </Button>
            <Button onClick={() => { setEditing(null); setShowForm(true); }} className="btn-brand rounded-sm" data-testid="add-property-btn">
              <Plus size={15} className="mr-1.5" /> Add Property
            </Button>
          </div>
        )}
        {embedded && isTeam && !isAdmin && (
          <div className="flex items-center gap-2 justify-end mb-4" data-testid="team-embedded-actions">
            <SharePlansButton variant="outline" size="sm" />
          </div>
        )}

        <Tabs value={queryTab} onValueChange={onTabSwitch} data-testid="admin-tabs">
          {!embedded && (
          <TabsList className="bg-white border border-[#E4E4E7] rounded-sm flex-wrap h-auto">
            <TabsTrigger value="overview" className="rounded-sm" data-testid="tab-overview">
              <BarChart3 size={13} className="mr-1.5" /> Overview
            </TabsTrigger>
            <TabsTrigger value="listings" className="rounded-sm" data-testid="tab-listings">Listings ({properties.length})</TabsTrigger>
            <TabsTrigger value="pending" className="rounded-sm" data-testid="tab-pending">
              <ScrollText size={13} className="mr-1.5" /> Approvals
              {pending.length > 0 && <span className="ml-1.5 bg-[#F59E0B] text-white text-[10px] px-1.5 rounded-full">{pending.length}</span>}
            </TabsTrigger>
            <TabsTrigger value="users" className="rounded-sm" data-testid="tab-users">
              <Users size={13} className="mr-1.5" /> Users ({users.length})
            </TabsTrigger>
            <TabsTrigger value="dealers" className="rounded-sm" data-testid="tab-dealers">
              <BadgeCheck size={13} className="mr-1.5" /> Dealers / KYC
            </TabsTrigger>
            <TabsTrigger value="crm" className="rounded-sm" data-testid="tab-crm">
              <Star size={13} className="mr-1.5" /> Smart CRM
            </TabsTrigger>
            <TabsTrigger value="create-user" className="rounded-sm" data-testid="tab-create-user">
              <UserPlus size={13} className="mr-1.5" /> Add Client
            </TabsTrigger>
            <TabsTrigger value="plans" className="rounded-sm" data-testid="tab-plans">
              <Tag size={13} className="mr-1.5" /> Plans
            </TabsTrigger>
            <TabsTrigger value="content" className="rounded-sm" data-testid="tab-content">
              <FileText size={13} className="mr-1.5" /> Content
            </TabsTrigger>
            <TabsTrigger value="pages" className="rounded-sm" data-testid="tab-pages">
              <FileText size={13} className="mr-1.5" /> Pages
            </TabsTrigger>
            <TabsTrigger value="transactions" className="rounded-sm" data-testid="tab-transactions">
              <Tag size={13} className="mr-1.5" /> Transactions
            </TabsTrigger>
            <TabsTrigger value="settings" className="rounded-sm" data-testid="tab-settings">
              <Tag size={13} className="mr-1.5" /> Settings
            </TabsTrigger>
            <TabsTrigger value="projects" className="rounded-sm" data-testid="tab-projects">
              <Building2 size={13} className="mr-1.5" /> Projects
            </TabsTrigger>
            <TabsTrigger value="team" className="rounded-sm" data-testid="tab-team">
              <Users size={13} className="mr-1.5" /> Team
            </TabsTrigger>
            <TabsTrigger value="filters" className="rounded-sm" data-testid="tab-filters">
              <Tag size={13} className="mr-1.5" /> Filters
            </TabsTrigger>
            <TabsTrigger value="locations" className="rounded-sm" data-testid="tab-locations">
              <BadgeCheck size={13} className="mr-1.5" /> Locations
            </TabsTrigger>
            <TabsTrigger value="testimonials" className="rounded-sm" data-testid="tab-testimonials">
              <Star size={13} className="mr-1.5" /> Testimonials
            </TabsTrigger>
            <TabsTrigger value="shortlists" className="rounded-sm" data-testid="tab-shortlists">
              <ListChecks size={13} className="mr-1.5" /> Shortlists
            </TabsTrigger>
            <TabsTrigger value="notifications" className="rounded-sm" data-testid="tab-notifications">
              Notifications {unreadCount > 0 && <span className="ml-1.5 bg-[#0055FF] text-white text-[10px] px-1.5 rounded-full">{unreadCount}</span>}
            </TabsTrigger>
          </TabsList>
          )}

          <TabsContent value="overview" className="mt-5">
            <AdminOverviewTab />
          </TabsContent>

          <TabsContent value="listings" className="mt-5">
            {/* v2.71.100 — Server-side paginated, filterable listings. The
                legacy client-side filtered table is replaced with this
                component to scale beyond a few hundred listings.
                v2.74 — onEdit now ALWAYS fetches the full property doc first
                because /api/admin/listings returns a thin projection (missing
                videos/custom_fields/intent details/coords/tags/etc.). Without
                the fetch, opening Edit → Save would wipe those fields via the
                PUT merge. */}
            <AdminListingsTab
              onEdit={async (p) => {
                try {
                  const r = await api.get(`/properties/${p.id}`);
                  setEditing(r.data);
                  setShowForm(true);
                } catch (e) {
                  toast.error(apiErrorMsg(e, "Could not load property for editing"));
                }
              }}
              onApprove={approve}
              onReject={reject}
              onMarkSold={markSold}
              onMarkAvailable={markAvailable}
              onDelete={del}
              onToggleFeatured={toggleFeatured}
            />
          </TabsContent>

          <TabsContent value="pending" className="mt-5">
            <AdminApprovalsTab />
          </TabsContent>

          <TabsContent value="users" className="mt-5">
            <div className="md:hidden text-[11px] text-[#A1A1AA] mb-2 flex items-center gap-1">
              <span>←</span> swipe to see more columns <span>→</span>
            </div>
            {/* v2.71.98 — Advanced role-based dropdown filter.
                Lists every standard customer role (plus "Owners" for future
                use). Changing the dropdown updates the `role=` URL param so
                the table re-renders instantly via the existing
                `filteredUsers` memo. Pagination resets to page 1
                automatically on every filter change. */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3" data-testid="admin-users-toolbar">
              <div className="flex items-center gap-2">
                <label htmlFor="admin-users-role-filter" className="text-[12px] font-semibold text-[#27272A]">
                  Filter:
                </label>
                <select
                  id="admin-users-role-filter"
                  value={fUserRole || "all"}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  className="h-9 rounded-sm border border-[#E4E4E7] bg-white px-3 text-[13px] font-medium focus:outline-none focus:ring-2 focus:ring-[#0055FF]/30 focus:border-[#0055FF] min-w-[160px]"
                  data-testid="admin-users-role-dropdown"
                >
                  <option value="all">All Users</option>
                  <option value="client">Clients</option>
                  <option value="dealer">Dealers</option>
                  <option value="builder">Builders</option>
                  <option value="owner">Owners</option>
                </select>
                <span className="text-[12px] text-[#52525B] ml-2" data-testid="admin-users-count">
                  {filteredUsers.length} {filteredUsers.length === 1 ? "user" : "users"}
                  {fUserRole && <span className="text-[#A1A1AA]"> of {users.length}</span>}
                </span>
              </div>
            </div>
            {/* v2.71.9 — Active URL-filter chip (Users) */}
            {hasUserFilter && (
              <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="users-active-filters">
                <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#52525B]">Active filter:</span>
                {fUserRole && (
                  <button onClick={() => clearAdminFilter(["role"])} className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[#0055FF] bg-[#EFF6FF] border border-[#BFDBFE] hover:bg-[#DBEAFE] rounded-full px-3 py-1" data-testid="filter-role-chip">
                    Role: {fUserRole} <X size={11} />
                  </button>
                )}
                {fUserFilter === "active_subs" && (
                  <button onClick={() => clearAdminFilter(["filter"])} className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[#0055FF] bg-[#EFF6FF] border border-[#BFDBFE] hover:bg-[#DBEAFE] rounded-full px-3 py-1" data-testid="filter-active-subs-chip">
                    Active subscriptions only <X size={11} />
                  </button>
                )}
                <span className="text-[11.5px] text-[#A1A1AA] ml-1">
                  Showing {filteredUsers.length} of {users.length}
                </span>
              </div>
            )}
            {/* v2.48 — Bulk select for users (skips self + last-admin server-side) */}
            <BulkSelectBar
              selectedIds={selectedUsers}
              setSelectedIds={setSelectedUsers}
              allIds={filteredUsers.map((u) => u.id)}
              onBulkDelete={bulkDeleteUsers}
              busy={bulkBusy}
              noun="users"
              testidPrefix="users-bulk"
            />
            <div className="bg-white border border-[#E4E4E7] rounded-sm overflow-x-auto allow-x-scroll" data-testid="admin-users-scroll">
              <table className="w-full text-sm min-w-[920px]">
                <thead className="bg-[#F4F4F5] border-b border-[#E4E4E7]">
                  <tr className="text-left text-[10px] uppercase tracking-wider text-[#52525B]">
                    <th className="p-3 w-8"></th>
                    <th className="p-3 font-semibold">User ID</th>
                    <th className="p-3 font-semibold">Name</th>
                    <th className="p-3 font-semibold">Email</th>
                    <th className="p-3 font-semibold">Phone</th>
                    <th className="p-3 font-semibold">Role</th>
                    <th className="p-3 font-semibold">Subscription</th>
                    <th className="p-3 font-semibold">Registered</th>
                    <th className="p-3 font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedUsers.length === 0 && (
                    <tr><td colSpan={9} className="p-10 text-center text-[13px] text-[#A1A1AA]" data-testid="admin-users-empty">
                      No users match the current filter.
                    </td></tr>
                  )}
                  {pagedUsers.map((u) => (
                    <tr key={u.id} className={`border-b border-[#F4F4F5] ${u.blocked ? "bg-[#FEE2E2]/40" : ""}`} data-testid={`user-row-${u.id}`}>
                      <td className="p-3">
                        {u.id === adminUserId ? (
                          <span className="text-[9px] uppercase tracking-wider font-bold text-zinc-400" title="You">YOU</span>
                        ) : (
                          <RowCheckbox id={u.id} selectedIds={selectedUsers} setSelectedIds={setSelectedUsers} testid={`user-check-${u.id}`} />
                        )}
                      </td>
                      <td className="p-3 font-mono-data text-[11.5px] text-[#52525B] whitespace-nowrap" title={u.id}>
                        {String(u.id).slice(0, 8)}…
                      </td>
                      <td className="p-3 font-semibold">
                        {/* v2.71.102 — Clickable name opens the User Profile
                            page. Permissions / Block / Delete remain as
                            separate action buttons on the right (unchanged). */}
                        <Link
                          to={`/admin/users/${u.id}`}
                          className="text-[#0055FF] hover:underline"
                          data-testid={`user-row-name-${u.id}`}
                        >
                          {u.name}
                        </Link>
                        {u.blocked && (
                          <span className="ml-2 text-[10px] uppercase tracking-wider font-semibold bg-[#DC2626] text-white px-1.5 py-0.5 rounded-sm" data-testid={`user-blocked-${u.id}`}>
                            Blocked
                          </span>
                        )}
                      </td>
                      <td className="p-3 text-[#52525B]">{u.email}</td>
                      <td className="p-3 text-[#52525B] font-mono-data text-[12px]" data-testid={`user-phone-${u.id}`}>
                        {u.phone ? (
                          <a href={`tel:${u.phone}`} className="hover:text-[#0055FF]">{u.phone}</a>
                        ) : (
                          <span className="text-[#A1A1AA]">—</span>
                        )}
                      </td>
                      <td className="p-3">
                        <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 border rounded-sm ${
                          u.role === "admin" ? "bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]" :
                          u.role === "dealer" ? "bg-[#EEF4FF] text-[#0044CC] border-[#C7D9FF]" :
                          u.role === "builder" ? "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]" :
                          u.role === "team" ? "bg-[#F0F9FF] text-[#075985] border-[#BAE6FD]" :
                          "bg-[#F4F4F5] text-[#52525B] border-[#E4E4E7]"
                        }`}>{u.role}</span>
                      </td>
                      <td className="p-3 text-[12px]">
                        {u.subscription?.tier ? (
                          <span className="font-semibold text-[#0055FF]">{u.subscription.tier}</span>
                        ) : (
                          <span className="text-[#A1A1AA]">—</span>
                        )}
                      </td>
                      <td className="p-3 text-[11px] text-[#A1A1AA]">{u.created_at?.slice(0, 10)}</td>
                      <td className="p-3 text-right whitespace-nowrap">
                        {u.blocked ? (
                          <Button
                            size="sm"
                            onClick={() => toggleBlock(u)}
                            className="text-[11px] h-7 mr-1.5 bg-[#16A34A] hover:bg-[#15803D] text-white rounded-sm font-semibold"
                            data-testid={`toggle-block-${u.id}`}
                            disabled={u.role === "admin"}
                            title={u.role === "admin" ? "Cannot unblock an admin from this UI" : "Unblock — restore login access"}
                          >
                            <Unlock size={11} className="mr-1" /> Unblock
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            onClick={() => toggleBlock(u)}
                            className="text-[11px] h-7 mr-1.5 bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-sm font-semibold"
                            data-testid={`toggle-block-${u.id}`}
                            disabled={u.role === "admin"}
                            title={u.role === "admin" ? "Cannot block an admin from this UI" : "Block — prevent login & access"}
                          >
                            <Lock size={11} className="mr-1" /> Block
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setPermTarget(u)}
                          className="text-[11px] h-7 mr-1.5 rounded-sm"
                          disabled={u.role === "admin"}
                          title={u.role === "admin" ? "Admin permissions can't be restricted" : "Manage edit/delete permissions"}
                          data-testid={`permissions-${u.id}`}
                        >
                          <ShieldCheck size={11} className="mr-1" /> Permissions
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => deleteUser(u)}
                          className="text-[11px] h-7 text-[#DC2626] hover:text-[#991B1B] hover:bg-[#FEE2E2]"
                          data-testid={`delete-user-${u.id}`}
                        >
                          <Trash2 size={12} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* v2.71.98 — Pagination footer (20 users / page) */}
            {filteredUsers.length > USERS_PER_PAGE && (
              <div className="mt-3 flex items-center justify-between flex-wrap gap-3" data-testid="admin-users-pagination">
                <div className="text-[12px] text-[#52525B]">
                  Showing <b className="text-[#27272A]">{(userPage - 1) * USERS_PER_PAGE + 1}</b>–
                  <b className="text-[#27272A]">{Math.min(userPage * USERS_PER_PAGE, filteredUsers.length)}</b> of{" "}
                  <b className="text-[#27272A]">{filteredUsers.length}</b>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setUserPage((p) => Math.max(1, p - 1))}
                    disabled={userPage === 1}
                    className="h-8 text-[12px] rounded-sm"
                    data-testid="admin-users-page-prev"
                  >
                    ← Prev
                  </Button>
                  <span className="text-[12px] font-semibold text-[#27272A] px-3" data-testid="admin-users-page-info">
                    Page {userPage} of {userPageCount}
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setUserPage((p) => Math.min(userPageCount, p + 1))}
                    disabled={userPage === userPageCount}
                    className="h-8 text-[12px] rounded-sm"
                    data-testid="admin-users-page-next"
                  >
                    Next →
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="notifications" className="mt-5">
            <div className="flex justify-end mb-3 gap-2">
              <Button variant="outline" size="sm" onClick={clearReadNotifs} className="rounded-sm text-[#DC2626] border-[#FECACA]" data-testid="clear-read-notifs-btn">
                <Trash2 size={11} className="mr-1" /> Clear all read
              </Button>
              <Button variant="outline" size="sm" onClick={markAllRead} className="rounded-sm" data-testid="mark-all-read-btn">Mark all read</Button>
            </div>
            {/* v2.48 — Bulk select for notifications */}
            <BulkSelectBar
              selectedIds={selectedNotifs}
              setSelectedIds={setSelectedNotifs}
              allIds={notifications.map((n) => n.id)}
              onBulkDelete={bulkDeleteNotifs}
              busy={bulkBusy}
              noun="notifications"
              testidPrefix="notifs-bulk"
            />
            <div className="space-y-2">
              {notifications.length === 0 && (
                <div className="text-center py-10 text-[#A1A1AA] bg-white border border-dashed border-[#E4E4E7] rounded-sm">No notifications.</div>
              )}
              {notifications.map((n) => (
                <div key={n.id} className={`p-3 rounded-sm border flex items-start gap-3 ${n.read ? "bg-white border-[#E4E4E7]" : "bg-[#EEF4FF] border-[#C7D9FF]"}`} data-testid={`notif-${n.id}`}>
                  <RowCheckbox id={n.id} selectedIds={selectedNotifs} setSelectedIds={setSelectedNotifs} testid={`notif-check-${n.id}`} />
                  <Bell size={14} className={n.read ? "text-[#A1A1AA] mt-0.5" : "text-[#0055FF] mt-0.5"} />
                  <div className="flex-1">
                    <div className="text-sm text-[#09090B]">{n.message}</div>
                    <div className="text-[11px] text-[#A1A1AA] mt-0.5">{new Date(n.created_at).toLocaleString()}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => deleteNotif(n.id)}
                    className="p-1.5 rounded-md hover:bg-red-50 text-zinc-400 hover:text-red-600 transition-colors"
                    title="Delete notification"
                    data-testid={`notif-delete-${n.id}`}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="shortlists" className="mt-5">
            <ShortlistsTab allProperties={properties} />
          </TabsContent>

          <TabsContent value="dealers" className="mt-5">
            <AdminDealersTab />
          </TabsContent>

          <TabsContent value="crm" className="mt-5">
            <LeadsCRMTab />
          </TabsContent>

          <TabsContent value="create-user" className="mt-5">
            <AdminCreateUserTab />
          </TabsContent>

          <TabsContent value="plans" className="mt-5">
            <AdminUnifiedPlansTab />
          </TabsContent>

          <TabsContent value="content" className="mt-5">
            <AdminContentTab />
          </TabsContent>

          <TabsContent value="pages" className="mt-5">
            <AdminPagesTab />
          </TabsContent>

          <TabsContent value="transactions" className="mt-5">
            <AdminTransactionsTab />
          </TabsContent>

          <TabsContent value="settings" className="mt-5">
            <AdminSettingsTab />
          </TabsContent>

          <TabsContent value="projects" className="mt-5">
            <AdminProjectsTab />
          </TabsContent>

          <TabsContent value="team" className="mt-5">
            <AdminTeamTab />
          </TabsContent>

          <TabsContent value="filters" className="mt-5">
            <AdminFiltersTab />
          </TabsContent>

          <TabsContent value="locations" className="mt-5">
            <AdminLocationsTab />
          </TabsContent>

          <TabsContent value="testimonials" className="mt-5">
            <AdminTestimonialsTab />
          </TabsContent>
        </Tabs>
      </div>

      {/* v2.72 — UnifiedSubmissionForm emits dataSync events itself on save;
          BulkImportDialog announces here. No manual reload plumbing needed. */}
      <PropertyForm open={showForm} onClose={() => { setShowForm(false); setEditStep(null); }} initial={editing} initialStep={editStep} />
      <BulkImportDialog open={showBulk} onClose={() => setShowBulk(false)} onDone={() => emitDataChange(["properties", "approvals", "dashboard"])} />
      <AdminPermissionsModal
        user={permTarget}
        open={!!permTarget}
        onClose={() => setPermTarget(null)}
        onSaved={() => load()}
      />
      {/* v2.71.55 — Bulk "Add to shortlist" from admin listings selection */}
      <AdminShortlistFlow
        open={bulkShortlistOpen}
        onOpenChange={(v) => { setBulkShortlistOpen(v); if (!v) setSelectedListings(new Set()); }}
        mode="bulk"
        initialPropertyIds={Array.from(selectedListings)}
        allProperties={properties}
        onSaved={() => { setSelectedListings(new Set()); }}
      />
    </div>
  );
}
